import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from './useAuth';

// Reads ?fund= and ?stream= from the URL, falls back to localStorage,
// then to the user's first fund in fund_access if neither is present.
//
// Admin users with empty fund_access see all funds (activeFundId = null).
// Non-admin users with empty fund_access get activeFundId = null and should
// be shown an empty state by the consuming component.
export function useActiveFund() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { userProfile } = useAuth();

  const fundAccess = userProfile?.fund_access || [];
  const currentRoles = userProfile?.roles ||
    (userProfile?.role ? [userProfile.role] : []);
  const isAdmin = currentRoles.includes('admin');

  // URL param takes precedence, then localStorage, then first fund in access list
  const storedFund = typeof window !== 'undefined'
    ? localStorage.getItem('mia_active_fund')
    : null;

  const activeFundId = searchParams.get('fund') ||
    storedFund ||
    (fundAccess.length > 0 ? fundAccess[0] : null);

  const activeStreamId = searchParams.get('stream') || null;

  // Persist the active fund to localStorage whenever it changes
  useEffect(() => {
    if (activeFundId) {
      localStorage.setItem('mia_active_fund', activeFundId);
    }
  }, [activeFundId]);

  const setActiveFund = (fundId, streamId = null) => {
    const params = new URLSearchParams(searchParams);
    params.set('fund', fundId);
    if (streamId) {
      params.set('stream', streamId);
    } else {
      params.delete('stream');
    }
    setSearchParams(params);
  };

  // Clear the active fund → "All Funds". Removes the URL fund/stream params AND
  // the localStorage key so that, after reload, the fallback chain resolves to
  // null for an admin (no URL param, no stored fund, empty fund_access → null).
  // For non-admins the fund_access[0] fallback is unchanged; this is only wired
  // into the admin switcher branch.
  const clearActiveFund = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('fund');
    params.delete('stream');
    setSearchParams(params);
    if (typeof window !== 'undefined') {
      localStorage.removeItem('mia_active_fund');
    }
  };

  return { activeFundId, activeStreamId, setActiveFund, clearActiveFund, fundAccess, isAdmin };
}
